"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, RefreshCw, Flame, ShieldCheck, PhoneCall, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const services = [{
  id: "repair",
  icon: Wrench,
  title: "Water Heater Repair",
  badge: "Emergency Diagnostic",
  description: "Rapid troubleshooting and repair for no hot water, pilot light failures, tank leaks, faulty thermostats, and strange knocking noises.",
  points: "Same-day diagnostics, all major gas & electric models"
}, {
  id: "replacement",
  icon: RefreshCw,
  title: "System Replacement",
  badge: "High-Efficiency Upgrades",
  description: "Guidance and turnkey replacement when your existing unit is rusted, leaking from the tank base, or beyond its reliable lifespan.",
  points: "Upfront pricing, modern energy-saving models"
}, {
  id: "installation",
  icon: Flame,
  title: "New Installation",
  badge: "Tank & Tankless",
  description: "Precision installation for tankless and conventional storage systems, fully compliant with Georgia plumbing and safety codes.",
  points: "Gas & electric hookups, expansion tank compliance"
}, {
  id: "maintenance",
  icon: ShieldCheck,
  title: "Preventive Maintenance",
  badge: "System Protection",
  description: "Annual sediment flushing, sacrificial anode rod replacement, and pressure relief valve testing to prevent sudden catastrophic leaks.",
  points: "Extends lifespan, preserves factory warranties"
}];
export default function WaterHeater() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="water-heater-services" data-nav="light" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8 lg:px-10">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1 text-xs font-bold tracking-wider text-secondary-foreground uppercase mb-3">
              <span className="inline-block size-2 rounded-full bg-accent" />
              Jonesboro Water Heater Specialists
            </div>
            <h2 className="font-default text-3xl md:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-[1.15]">
              Dependable Hot Water Solutions for Your Home
            </h2>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              No hot water or leaking tank? Our licensed technicians deliver fast, diagnostic-led water heater repairs, replacements, and routine maintenance across Jonesboro.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a href="tel:4708479076" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto rounded-full bg-accent text-accent-foreground font-bold hover:brightness-105 shadow-sm transition-[color,background-color,border-color,box-shadow]">
                <PhoneCall className="size-4" />
                Call (470) 847-9076
              </Button>
            </a>
            <Button asChild variant="outline" size="lg" className="rounded-full border-border bg-card text-foreground hover:bg-muted font-semibold transition-[color,background-color,border-color,box-shadow]">
              <Link to="/water-heater/">
                View Full Services
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* 4 Service Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} variants={itemVariants} className="h-full">
                <Card data-index={i} className="h-full border border-border bg-card hover:bg-background transition-[background-color,border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md flex flex-col justify-between rounded-xl p-6 relative overflow-hidden group">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-transparent group-hover:bg-accent transition-colors" />

                  <CardContent className="p-0 flex flex-col h-full">
                    <div className="flex items-center justify-between gap-3 mb-5">
                      <div className="flex size-12 items-center justify-center rounded-xl bg-secondary text-primary">
                        <IconComponent className="size-6" />
                      </div>
                      <span className="text-xs font-semibold text-muted-foreground bg-muted px-2.5 py-1 rounded-md">
                        {service.badge}
                      </span>
                    </div>

                    <h3 className="font-default text-xl font-bold text-card-foreground mb-2.5 group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>

                    <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-grow">
                      {service.description}
                    </p>

                    <div className="pt-4 border-t border-border/80 mt-auto">
                      <div className="flex items-start gap-2 mb-4">
                        <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                        <span className="text-xs font-medium text-muted-foreground">
                          {service.points}
                        </span>
                      </div>

                      <Button asChild variant="ghost" size="sm" className="w-full justify-between px-3 text-primary hover:bg-secondary/60 hover:text-primary font-semibold rounded-lg group-hover:translate-x-0.5 transition-[color,background-color,border-color,box-shadow,transform]">
                        <Link to="/water-heater/">
                          <span>Service Details</span>
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Emergency Triage Banner Strip */}
        <div className="mt-10 rounded-2xl bg-secondary/50 border border-border p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="size-3 rounded-full bg-destructive animate-pulse shrink-0" />
            <div>
              <h4 className="font-default font-bold text-foreground text-base md:text-lg">
                Water Heater Leaking From The Bottom or Making Loud Popping Sounds?
              </h4>
              <p className="text-sm text-muted-foreground mt-0.5">
                Shut off your water inlet valve immediately and contact our local 24/7 emergency dispatch.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
            <a href="tel:4708479076" className="w-full md:w-auto">
              <Button size="default" className="w-full md:w-auto rounded-full bg-accent text-accent-foreground font-bold hover:brightness-105 transition-[color,background-color,border-color,box-shadow]">
                <PhoneCall className="size-4" />
                (470) 847-9076
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>;
}